import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useIncidentAlerts from '../../hooks/useIncidentAlerts';
import useDataSync from '../../hooks/useDataSync';
import GlobalSearch from './GlobalSearch';
import InstallButton from './InstallButton';
import ShareAppButton from './ShareAppButton';
import PushToggle from './PushToggle';
import SyncIndicator from './SyncIndicator';
import UpdateBanner from './UpdateBanner';
import { LayoutDashboard, MapPin, Handshake, Calculator, LogOut, FileText, Users, Menu, X, Target, AlertTriangle, ShieldCheck, Flag, Wrench, Calendar, Activity, BarChart3, Clock, ClipboardList, Zap, BookOpen, Receipt, Bell, Route, TrendingDown, Droplets, Mail, Building2, Map } from 'lucide-react';

const Logo = ({ small }) => (
  <Link to="/app" className="flex items-center gap-1">
    <span className={`text-white font-black tracking-tight lowercase ${small ? 'text-xl' : 'text-2xl'}`}>
      adqui<span className="text-[#A3FF12]">riam</span>
    </span>
    <svg width={small ? 16 : 18} height={small ? 16 : 18} viewBox="0 0 24 24" className="text-[#A3FF12] ml-0.5" fill="none">
      <path d="M4 4h16M4 12h16M4 20h10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
    </svg>
  </Link>
);

const navItems = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard, end: true },
  // ── ADQUISICIONES ──
  { header: 'Adquisiciones' },
  { to: '/app/prospeccion', label: 'Prospección', icon: MapPin },
  { to: '/app/prospecciones', label: 'SAR', icon: FileText },
  { to: '/app/ofrecimientos', label: 'Ofrecimientos', icon: Target },
  { to: '/app/negociaciones', label: 'Negociaciones', icon: Handshake },
  { to: '/app/capitalizaciones', label: 'Capitalizaciones', icon: Calculator },
  { to: '/app/bajada-renta', label: 'Bajada de renta', icon: TrendingDown },
  { to: '/app/gastos-electricos', label: 'Gastos eléctricos', icon: Zap },
  { to: '/app/facturacion', label: 'Facturación', icon: Receipt },
  { to: '/app/normativa', label: 'Normativa', icon: FileText },
  { to: '/app/emplazamientos', label: 'Emplazamientos', icon: Building2 },
  { to: '/app/heatmap', label: 'Heatmap nacional', icon: Map },
  // ── MANTENIMIENTO ──
  { header: 'Mantenimiento' },
  { to: '/app/mantenimiento', label: 'Panel', icon: Wrench, end: true },
  { to: '/app/incidencias', label: 'Incidencias', icon: AlertTriangle },
  { to: '/app/totem-dashboard', label: 'Dashboard TOTEM', icon: BarChart3 },
  { to: '/app/preventivo', label: 'Preventivo', icon: ShieldCheck },
  { to: '/app/conflictivos', label: 'Conflictivos', icon: Flag },
  { external: 'https://humidity-detective.emergent.host', label: 'Humedisity (peritaje Adquiriam)', icon: Droplets },
  // ── SEGUIMIENTO ──
  { header: 'Seguimiento' },
  { to: '/app/agenda', label: 'Agenda', icon: Calendar },
  { to: '/app/eventos', label: 'Eventos', icon: Activity },
  { to: '/app/fichaje', label: 'Fichaje', icon: Clock },
  { to: '/app/fichajes-mapa', label: 'Mapa fichajes', icon: Route },
  { to: '/app/ranking', label: 'Ranking cobertura', icon: BarChart3 },
  { to: '/app/ayuda', label: 'Ayuda', icon: BookOpen },
  { to: '/app/mensajes', label: 'Mensajes', icon: Mail, badgeKey: 'unread_msgs' },
  // ── ADMIN ──
  { header: 'Administración', adminOnly: true },
  { to: '/app/partes', label: 'Partes horarios', icon: ClipboardList, adminOnly: true },
  { to: '/app/admin', label: 'Usuarios', icon: Users, adminOnly: true },
  { to: '/app/dashboard-admin', label: 'Dashboard admin', icon: LayoutDashboard, adminOnly: true },
  { to: '/app/push-log', label: 'Push log', icon: Bell, adminOnly: true },
];

const SidebarContent = ({ user, logout, onItemClick, visibleNav, syncStatus, syncNow, badges }) => (
  <>
    <div className="px-6 py-6 border-b border-white/10">
      <Logo/>
    </div>
    <GlobalSearch onNavigate={onItemClick}/>
    <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
      {visibleNav.map((it, idx) => {
        if (it.header) {
          return (
            <div key={`h-${idx}`} className="px-2 pt-4 pb-1 text-[10px] font-black uppercase tracking-widest text-white/30">
              {it.header}
            </div>
          );
        }
        if (it.external) {
          return (
            <a key={`ext-${idx}`} href={it.external} target="_blank" rel="noreferrer" onClick={onItemClick}
              data-testid={`nav-ext-${it.label.toLowerCase().replace(/\s+/g,'-')}`}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-white/70 hover:bg-blue-500/10 hover:text-blue-300 border border-transparent hover:border-blue-500/30 transition-colors">
              <it.icon size={16}/> {it.label}
              <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-black">EXT</span>
            </a>
          );
        }
        const badge = it.badgeKey ? badges?.[it.badgeKey] : 0;
        return (
          <NavLink key={it.to} to={it.to} end={it.end} onClick={onItemClick} data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g,'-')}`}
            className={({isActive}) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                isActive ? 'bg-[#A3FF12]/10 text-[#A3FF12] border border-[#A3FF12]/30' : 'text-white/70 hover:bg-white/5 hover:text-white border border-transparent'
              }`}>
            <it.icon size={16}/> <span className="flex-1">{it.label}</span>
            {badge > 0 && (
              <span data-testid={`nav-badge-${it.badgeKey}`}
                className="bg-[#A3FF12] text-black text-[10px] font-black rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
    <InstallButton/>
    <ShareAppButton/>
    <PushToggle/>
    <SyncIndicator status={syncStatus} onSyncNow={syncNow}/>
    <div className="px-3 py-4 border-t border-white/10">
      <div className="flex items-center gap-3 px-3 py-2">
        <div className="w-9 h-9 rounded-full bg-[#A3FF12] text-black flex items-center justify-center font-black flex-shrink-0">
          {(user?.name || 'U').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-sm font-bold truncate">{user?.name}</div>
          <div className="text-white/40 text-xs truncate uppercase tracking-wide">{user?.role}</div>
        </div>
        <button onClick={logout} className="text-white/40 hover:text-[#A3FF12] transition-colors p-1" title="Salir">
          <LogOut size={18}/>
        </button>
      </div>
    </div>
  </>
);

const AppLayout = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState(null);
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const visibleNav = navItems.filter((it) => !it.adminOnly || user?.role === 'admin');

  useIncidentAlerts({ enabled: !!user });
  const { status: syncStatus, syncNow } = useDataSync({ enabled: !!user });
  useEffect(() => {
    const onNew = (e) => {
      setAlert({ count: e.detail.count, at: Date.now() });
      setTimeout(() => setAlert(null), 8000);
    };
    window.addEventListener('sf:new-incident', onNew);
    return () => window.removeEventListener('sf:new-incident', onNew);
  }, []);

  // Poll unread messages count (cada 30s)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const api = (await import('../../api/client')).default;
        const { data } = await api.get('/messages/unread-count');
        if (!cancelled) setUnreadMsgs(data?.count || 0);
      } catch { /* noop */ }
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user?.id]);

  const badges = { unread_msgs: unreadMsgs };

  return (
    <div className="min-h-screen bg-black text-white">
      <UpdateBanner/>
      {alert && (
        <div data-testid="incident-alert-banner"
          className="fixed bottom-4 right-4 z-[10000] bg-red-500/95 border border-red-400 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 max-w-xs backdrop-blur">
          <AlertTriangle size={18} className="text-white flex-shrink-0"/>
          <div className="min-w-0 flex-1">
            <div className="font-black text-sm leading-tight">{alert.count} incidencia(s) alta prioridad</div>
            <NavLink to="/app/incidencias" onClick={() => setAlert(null)}
              className="text-white/90 hover:text-white text-xs underline">Ver ahora →</NavLink>
          </div>
          <button onClick={() => setAlert(null)} data-testid="btn-close-alert-banner"
            aria-label="Cerrar aviso"
            className="text-white/80 hover:text-white flex-shrink-0 p-0.5">
            <X size={16}/>
          </button>
        </div>
      )}
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-40 bg-black/90 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <Logo small/>
        <button onClick={() => setOpen(true)} className="p-2 text-white hover:text-[#A3FF12] transition-colors" aria-label="Abrir menú">
          <Menu size={24}/>
        </button>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-[#080808] border-r border-white/10 flex-col fixed h-screen z-30">
        <SidebarContent user={user} logout={logout} visibleNav={visibleNav} syncStatus={syncStatus} syncNow={syncNow} badges={badges}/>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}/>
          <aside className="relative w-72 max-w-[80vw] bg-[#080808] border-r border-white/10 flex flex-col h-full animate-in slide-in-from-left">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-white/60 hover:text-white p-1.5 rounded-lg z-10" aria-label="Cerrar">
              <X size={22}/>
            </button>
            <SidebarContent user={user} logout={logout} onItemClick={() => setOpen(false)} visibleNav={visibleNav} syncStatus={syncStatus} syncNow={syncNow} badges={badges}/>
          </aside>
        </div>
      )}

      <main className="lg:ml-64 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout;
